import api from "./api";

// All paths are relative to the configured API base URL. The backend mounts
// every route under /api/v1.
const FEED_PATH = "api/v1/feed/";
const GUEST_FEED_PATH = "api/v1/prompt-posts/guest-feed";
const PROMPT_FEED_PATH = "api/v1/prompt-posts/";
const VOTE_PATH = "api/v1/prompt-posts/vote";
const MARK_SEEN_PATH = (matchId) => `api/v1/prompt-posts/mark-seen/${matchId}`;
const MATCH_COMMENTS_PATH = (matchId) =>
  `api/v1/matches/${matchId}/list-comments`;
const POST_COMMENT_PATH = (matchId) => `api/v1/matches/${matchId}/comment`;
const LIKE_PATH = (postId) => `api/v1/post/like/${postId}`;
const UNLIKE_PATH = (postId) => `api/v1/post/unlike/${postId}`;
const LEADERBOARD_PATH = "api/v1/leaderboard/";

const GUEST_LIKE_QUEUE_KEY = "pagent_guest_like_queue";

// Personalized feed — auth required. Returns posts from users you follow,
// cursor-paginated. Shape: { posts: [...], nextId }.
export async function getMyFeed({ limit = 10, nextId } = {}) {
  const params = { limit };
  if (nextId) params.nextId = nextId;
  const res = await api.get(FEED_PATH, { params });
  const data = res?.data?.data || {};
  return {
    posts: Array.isArray(data.posts) ? data.posts : [],
    nextId: data.nextId ?? null,
  };
}

// Public guest feed — head-to-head completed matches. Shape:
// { matches: [...], nextId: null } (no pagination, ~6 max).
export async function getGuestFeed() {
  const res = await api.get(GUEST_FEED_PATH);
  const data = res?.data?.data || {};
  return {
    matches: Array.isArray(data.matches) ? data.matches : [],
    nextId: data.nextId ?? null,
  };
}

// Authed votable feed — IN_PROGRESS matchups only (excludes your own + blocked
// users). The list PREPENDS live entries: [liveRooms, postMatches].flat().
// Each item has a `type` field — 'LIVE' items use a different (live-room) shape,
// so callers must branch on `type`. Authed items have NO `matchId` field; use
// `_id` as the match id when voting. Shape: { matches: [...], nextId }.
export async function getPromptFeed({ limit = 10, nextId } = {}) {
  const params = { limit };
  if (nextId) params.nextId = nextId;
  const res = await api.get(PROMPT_FEED_PATH, { params });
  const data = res?.data?.data || {};
  return {
    matches: Array.isArray(data.matches) ? data.matches : [],
    nextId: data.nextId ?? null,
  };
}

// Cast a vote on an IN_PROGRESS match (authed only).
// - Post matches (prompt/category/contest): { matchId, postId } where postId is
//   the chosen submission; the backend infers the winning player.
// - LIVE matches: { matchId, userId } where userId is the chosen player.
// `matchId` is the authed item's `_id` (NOT a `matchId` field).
// Resolves to { leadingUser, totalViews } — `leadingUser` is the real player id
// who is ahead after this vote (no per-side counts are returned). Logical
// failures (e.g. MatchHasEnded, MatchAlreadyVoted) reject with an Error.
export async function voteOnMatch({ matchId, postId, userId } = {}) {
  if (!matchId) throw new Error("matchId is required to vote");
  if (!postId && !userId) {
    throw new Error("voteOnMatch needs a postId or userId");
  }
  const body = { matchId };
  if (postId) body.postId = postId;
  if (userId) body.userId = userId;
  try {
    const res = await api.post(VOTE_PATH, body);
    return res?.data?.data || {};
  } catch (err) {
    const message =
      err?.response?.data?.message || err?.message || "Vote failed";
    const wrapped = new Error(message);
    wrapped.cause = err;
    throw wrapped;
  }
}

// Mark an IN_PROGRESS match as SEEN for the current (authed) user so the backend
// excludes it from subsequent `GET /prompt-posts/` pages (the feed filters on the
// `seenmatches` collection). `matchId` is the match `_id`. Authed-only route, so
// callers must gate on auth. Fire-and-forget: a repeat call is rejected with
// "already seen" and network blips aren't actionable here, so we swallow errors.
export async function markMatchSeen(matchId) {
  if (!matchId) return;
  try {
    await api.get(MARK_SEEN_PATH(matchId));
  } catch {
    // already-seen / transient failure — nothing the caller can do, ignore.
  }
}

// Leaderboard (authed only — the route is behind `authorizeUser`). `POST
// /leaderboard/` returns the TOP-100 only. Omit `categoryId` for the GLOBAL
// ("worldwide") board; pass it for a per-category board. `type` is required by
// the backend Joi schema (day|week|month) — we default to the broadest window
// ('month'). Response envelope: `data.leaderboard` = [{ rank, userId, username,
// stars, ... }]. Returns [] for guests/failures so callers can fall back. POST
// (not GET) so the global `_ts` cache-buster never applies.
export async function getLeaderboard({ type = "month", categoryId, searchBy } = {}) {
  const body = { type };
  if (categoryId) body.categoryId = categoryId;
  if (searchBy) body.searchBy = searchBy;
  try {
    const res = await api.post(LEADERBOARD_PATH, body);
    const list = res?.data?.data?.leaderboard;
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

// Voted-match bookkeeping. The authed `/prompt-posts/` feed does NOT exclude
// matches the requesting user already voted on, nor does it return a per-match
// "voted by me" flag (only LIVE rooms get server-side voted-exclusion). So the
// web client persists the matchIds it has voted on and filters them out of
// freshly-loaded pages. Scoped per user id so a shared browser doesn't leak one
// account's votes into another's feed.
const VOTED_MATCHES_KEY = "pagent_voted_matches";
const GUEST_VOTE_COUNT_KEY = "pagent_guest_vote_count";

// How many mock votes a guest can cast (each showing the result bar) before we
// prompt signup. Persists across sessions so refresh doesn't reset the preview.
export const GUEST_VOTE_LIMIT = 4;

export function getGuestVoteCount() {
  try {
    const raw = localStorage.getItem(GUEST_VOTE_COUNT_KEY);
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function incrementGuestVoteCount() {
  const next = getGuestVoteCount() + 1;
  try {
    localStorage.setItem(GUEST_VOTE_COUNT_KEY, String(next));
  } catch {
    // Quota/private mode — count still advances in-memory via caller state.
  }
  return next;
}

function votedMatchesKey() {
  try {
    const raw = localStorage.getItem("pagent_user");
    const user = raw ? JSON.parse(raw) : null;
    const id = user?.id || user?._id;
    return id ? `${VOTED_MATCHES_KEY}:${id}` : VOTED_MATCHES_KEY;
  } catch {
    return VOTED_MATCHES_KEY;
  }
}

export function getVotedMatchIds() {
  try {
    const raw = localStorage.getItem(votedMatchesKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function addVotedMatchId(matchId) {
  if (!matchId) return;
  const ids = getVotedMatchIds();
  if (ids.includes(matchId)) return;
  ids.push(matchId);
  try {
    localStorage.setItem(votedMatchesKey(), JSON.stringify(ids));
  } catch {
    // Storage may be unavailable (private mode / quota) — exclusion just
    // falls back to in-memory for this session.
  }
}

function readCommentList(res) {
  const data = res?.data?.data;
  const list = Array.isArray(data)
    ? data
    : data?.comments || data?.list || data?.results || [];
  return Array.isArray(list) ? list : [];
}

// Comments for a match (authed only — all comment routes require a user JWT, so
// this 401s for guests). Items: { _id, content, createdAt,
// userDetails: { _id, username, firstName, lastName, profilePicture } }.
// NOTE: the backend's list-comments validation requires `limit` (Joi
// .required()) AND rejects unknown query keys, so we send `limit` and disable
// the global `_ts` cache-buster (`cacheBust: false`) — otherwise it 400s.
export async function getMatchComments(matchId, { limit = 20, nextId } = {}) {
  if (!matchId) return [];
  const params = { limit };
  if (nextId) params.nextId = nextId;
  const res = await api.get(MATCH_COMMENTS_PATH(matchId), {
    params,
    cacheBust: false,
  });
  return readCommentList(res);
}

// Post a comment on a match (authed; match must be IN_PROGRESS). Returns the
// created comment from the response envelope when the backend provides it.
export async function postMatchComment(matchId, content) {
  const res = await api.post(POST_COMMENT_PATH(matchId), { content });
  return res?.data?.data;
}

export async function likePost(postId) {
  const res = await api.post(LIKE_PATH(postId));
  return res?.data;
}

export async function unlikePost(postId) {
  const res = await api.post(UNLIKE_PATH(postId));
  return res?.data;
}

// Guest-like queue: when a logged-out user taps the heart we hold it locally
// and replay through the real like endpoint after they sign in. The backend
// has no anonymous like endpoint, so this is the agreed fallback.
export function getGuestLikeQueue() {
  try {
    const raw = localStorage.getItem(GUEST_LIKE_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function setGuestLikeQueue(ids) {
  localStorage.setItem(GUEST_LIKE_QUEUE_KEY, JSON.stringify(ids));
}

export function queueGuestLike(postId) {
  if (!postId) return;
  const queue = getGuestLikeQueue();
  if (queue.includes(postId)) return;
  queue.push(postId);
  setGuestLikeQueue(queue);
}

export function unqueueGuestLike(postId) {
  if (!postId) return;
  const queue = getGuestLikeQueue().filter((id) => id !== postId);
  setGuestLikeQueue(queue);
}

export function isGuestLiked(postId) {
  return getGuestLikeQueue().includes(postId);
}

// Replay queued guest likes against the real backend. Best-effort: failures
// are swallowed so a single bad post id doesn't block the rest. The queue is
// always cleared at the end so we don't re-fire the same likes on every login.
export async function replayGuestLikes() {
  const queue = getGuestLikeQueue();
  if (queue.length === 0) return { replayed: 0, failed: 0 };

  let replayed = 0;
  let failed = 0;
  for (const postId of queue) {
    try {
      await likePost(postId);
      replayed += 1;
    } catch (err) {
      failed += 1;
      // eslint-disable-next-line no-console
      console.warn("[feedService] guest-like replay failed for", postId, err);
    }
  }
  setGuestLikeQueue([]);
  return { replayed, failed };
}
